import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuInformationBox} from '@Components/contextual-menu/components/contextual-menu-information-box';
import {InformationBoxType} from '@Components/contextual-menu/components/information-box';
import {Text, TextSize} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {showMessageGrowl} from '@Components/message-growl/message-growl';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetNumberOfSlidesForActiveSlideshowItem} from '@Hooks/poster-editor/useGetNumberOfSlidesForActiveSlideshowItem';

export function SlideshowApplyPropertiesInfobox(): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const numOfSlides = useGetNumberOfSlidesForActiveSlideshowItem();

  if (!activeItem || !activeSlide) {
    throw new Error('Active item or active slide is not defined in slideshow apply properties information box');
  }

  if (!activeItem.isSlideshow()) {
    throw new Error(`Expected active item to be of type slideshow, got ${activeItem.gitype} instead`);
  }

  if (!numOfSlides || numOfSlides < 2) {
    return null;
  }

  if (activeSlide.isTextSlide() && activeItem.slides.getNumberOfTextSlides() < 2) {
    return null;
  }
  if (activeSlide.isVideoSlide() && activeItem.slides.getNumberOfVideoSlides() < 2) {
    return null;
  }
  if (activeSlide.isImageSlide() && activeItem.slides.getNumberOfImageSlides() < 2) {
    return null;
  }

  const getText = (): string => {
    switch (activeSlide.gitype) {
      case ITEM_TYPE.TEXTSLIDE:
        return window.i18next.t('pmwjs_apply_to_all_text_slides');
      case ITEM_TYPE.IMAGESLIDE:
        return window.i18next.t('pmwjs_apply_to_all_photo_slides');
      case ITEM_TYPE.VIDEOSLIDE:
        return window.i18next.t('pmwjs_apply_to_all_video_slides');
      default:
        throw new Error('Unhandled ');
    }
  };

  const onClickHandler = (): void => {
    void applySidebarProps();
  };

  const applySidebarProps = async (): Promise<void> => {
    await activeItem.applyStylesToAllSlides();
    showMessageGrowl({text: window.i18next.t('pmwjs_applied')});
  };

  return (
    <ContextualMenuInformationBox id="slideshow-apply-properties-infobox" type={InformationBoxType.PRIMARY}>
      <div className="flex-v-row">
        <Text dangerouslySetInnerHTML val={getText()} size={TextSize.XSMALL} className="content-primary" />
        <Button onClick={onClickHandler} size={Size.SMALL} customClasses="spacing-m-t-3 spacing-m-b-1" type={Type.PRIMARY} text={window.i18next.t('pmwjs_apply')} />
      </div>
    </ContextualMenuInformationBox>
  );
}
